import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Building } from "lucide-react";

/**
 * Swipeable property photo gallery for the client portal.
 *
 * Renders one full-bleed image at a time inside a fixed-height frame and
 * exposes:
 *   • Left/right chevron buttons that wrap around (desktop mouse use)
 *   • Touch-swipe gestures (mobile) with a 40-pixel deadband so incidental
 *     scrolls don't flip the slide
 *   • Dot indicators + a "3 / 8" counter pill so buyers always know where
 *     they are inside the gallery
 *   • Keyboard ← / → when the gallery is focused
 *
 * Falls back to a single background image when only one photo is passed,
 * and to a Building icon placeholder when no photos exist — matches the
 * previous single-image card look so the empty state is unchanged.
 */
export default function PropertyGallery({ photos = [], className = "", heightClass = "h-40 sm:h-48", testId = "property-gallery" }) {
  const clean = (photos || []).filter(u => typeof u === "string" && u.trim());
  const total = clean.length;
  const [i, setI] = useState(0);
  const touchStartX = useRef(null);
  const containerRef = useRef(null);

  // Clamp the current index if the parent hot-swaps the photo list
  // (rare, but keeps the counter honest).
  useEffect(() => {
    if (i >= total) setI(0);
  }, [total, i]);

  const go = (delta) => {
    if (total < 2) return;
    setI(v => (v + delta + total) % total);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;
    const end = e.changedTouches?.[0]?.clientX ?? start;
    const dx = end - start;
    if (Math.abs(dx) < 40) return;  // deadband — ignore a tap or scroll
    go(dx < 0 ? 1 : -1);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
  };

  if (total === 0) {
    return (
      <div
        className={`${heightClass} bg-slate-100 flex items-center justify-center ${className}`}
        data-testid={`${testId}-empty`}
      >
        <Building className="h-12 w-12 text-slate-300" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={total > 1 ? 0 : -1}
      role={total > 1 ? "region" : undefined}
      aria-roledescription={total > 1 ? "carousel" : undefined}
      aria-label={total > 1 ? `Property photos (${i + 1} of ${total})` : undefined}
      onKeyDown={onKeyDown}
      onTouchStart={total > 1 ? onTouchStart : undefined}
      onTouchEnd={total > 1 ? onTouchEnd : undefined}
      className={`relative ${heightClass} bg-slate-100 overflow-hidden select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 ${className}`}
      data-testid={testId}
    >
      {/* Slides — kept mounted with opacity swap so the browser caches
          decode work and swiping feels instant. Keyed on the photo URL
          (stable identity) instead of the array index so React doesn't
          reuse decoded canvases across a reorder. */}
      {clean.map((src, idx) => (
        <div
          key={src}
          aria-hidden={idx !== i}
          className={`absolute inset-0 transition-opacity duration-300 ease-out ${idx === i ? "opacity-100" : "opacity-0"}`}
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      ))}

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Previous photo"
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Next photo"
            data-testid={`${testId}-next`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          {/* Counter pill (top-right) */}
          <div
            className="absolute top-3 right-3 rounded-full bg-black/60 text-white text-[11px] font-semibold tracking-wide px-2.5 py-1 tabular-nums"
            data-testid={`${testId}-counter`}
          >
            {i + 1} / {total}
          </div>

          {/* Dots (bottom center) — same key strategy as the slides above. */}
          <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1.5" aria-hidden="true">
            {clean.map((src, idx) => (
              <button
                key={src}
                type="button"
                onClick={() => setI(idx)}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-white" : "w-1.5 bg-white/60 hover:bg-white/80"}`}
                aria-label={`Go to photo ${idx + 1}`}
                tabIndex={-1}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
